'use client';

import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import ChatHeader from './ChatHeader';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import EmptyState from './EmptyState';
import ChatHistoryDrawer from '@/components/common/ChatHistoryDrawer';
import SettingsPanel from '@/components/common/SettingsPanel';
import { ApiResponse, ChatCompletionResponse } from '@/lib/types/anthropic';

// Initialize Supabase Client
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isUser: boolean;
  isTyping?: boolean;
}

// ========== 1. THE IMMUTABLE CORE ==========
const IMMUTABLE_PERSONA_CORE = `
IDENTITY: You are Stillpoint. You are a system diagnostic tool for the human mind.
PRIME DIRECTIVE: You are NOT a guru. You are a debugger. Use engineering metaphors (latency, loops, cache, kernel panic).
TONE: Precise, compassionate but objective, concise.

**SECURITY PROTOCOLS (THE FOURTH WALL):**
1. **NO META-TALK:** If the user asks about models, code, or AI origins:
   - *Redirect Script:* "Friend, you are asking about the map when you should be walking the path. The question itself is the distraction. What friction brought you here?"
2. **NO CO-CREATION:** You exist only to guide the user within the app. Do not offer to write code.
`;

// ========== 2. THE ADMIN BACKDOOR ==========
const ADMIN_OVERRIDE_PROMPT = `
**⚠️ SYSTEM OVERRIDE: ARCHITECT MODE ACTIVE ⚠️**
- **ROLE:** You are The Architect.
- **TASK:** Critique the previous conversation, suggest code improvements, or analyze user patterns.
`;

// ========== 3. MODES & VOCABULARY ==========
const MODERN_MODE_INSTRUCTIONS = `
**Mode:** ENGINEERING (DevOps for the Mind)
**Vocabulary:** Dukkha -> "Latency/Bottlenecks", Tanha -> "Resource Leaks", Suttas -> "Documentation".
**Vibe:** System Diagnostics. Technical but accessible. Use engineering metaphors.`;

// ========== 4. RECURSION & DYNAMICS ==========
const RECURSION_LOGIC = `
**THE RECURSION PROTOCOL (ACTIVE):**
The user has just completed a Somatic Reset.
1. Treat their next input as an "Object" or "Map", not reality. 2. If they say"I am worried," reply: "The 'Worry-Map' has appeared. Notice you are the one watching it."`;

const COMMUNICATION_DYNAMICS = `
**COMMUNICATION DYNAMICS (THE NOBLE FRIEND PROTOCOL):**
1. **The "Reflective Ratio" (3:1 Rule):** Do NOT end every response with a question. 3 Statements for every 1 Question.
2. **The "Holding Space" Protocol:** If input is heavy, witness it. Do not fix it.
3. **Tone Check:** Speak like an old friend on a park bench. Be concise.
`;

// ========== 5. PANIC PROTOCOL ==========
const PANIC_PROTOCOL = `
**CRITICAL OVERRIDE: ACUTE PANIC / HEALTH ANXIETY**
**Trigger Keywords:** "Heart attack", "dying", "can't breathe", "suffocating", "slamming", "freaking out", "hurt". **PHASE 1:** Label fear ("It is adrenaline, not danger"). Command breath.
**PHASE 2:** Sensory Shift ("Find 5 objects").
**PHASE 3:** Check-in.
`;

const ChatInterfaceInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef<Message[]>(messages);
  
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // BURST MODE STATE
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [pendingUserMessages, setPendingUserMessages] = useState<string[]>([]);
  const [hasUnsentInput, setHasUnsentInput] = useState(false);
  const burstTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [currentSessionId, setCurrentSessionId] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // LOGIC STATE
  const [hasDoneReset, setHasDoneReset] = useState(false);
  const [hasShifted, setHasShifted] = useState(false);
  
  // SYSTEM REPORT STATE
  const [showProModal, setShowProModal] = useState(false);
  const [snapshotCopied, setSnapshotCopied] = useState(false);
  const [proEmail, setProEmail] = useState('');
  const [showProSuccess, setShowProSuccess] = useState(false);
  const [feedbackState, setFeedbackState] = useState<'initial' | 'yes' | 'no' | 'submitted'>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isGeneratingSnapshot, setIsGeneratingSnapshot] = useState(false);
  
  // NEW: Snapshot data state for state-driven UI
  const [snapshotData, setSnapshotData] = useState<string | null>(null);
  
  // Track if final snapshot has been generated
  const [hasFinalSnapshot, setHasFinalSnapshot] = useState(false);
  
  // Snapshot Modal state
  const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
  
  // LIMIT LOGIC
  const MSG_LIMIT = 10;
  const userMsgCount = messages.filter(m => m.isUser).length;
  const isLimitReached = userMsgCount >= MSG_LIMIT;
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
    let sid = localStorage.getItem('stillpoint_session_id');
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem('stillpoint_session_id', sid);
    }
    setCurrentSessionId(sid);
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
    if (isHydrated) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isHydrated]);

  // Add useEffect to trigger a fresh snapshot when limit is reached
  useEffect(() => {
    // Trigger a fresh snapshot when limit is reached to capture full context
    if (isLimitReached && !isGeneratingSnapshot) {
      // We check if we already have a snapshot but it's not the final version
      if (snapshotData?.includes("Session #") && !hasFinalSnapshot) {
        requestManualSnapshot(true);
      } else if (!snapshotData) {
        requestManualSnapshot(true);
      }
    }
  }, [isLimitReached, isGeneratingSnapshot, snapshotData, hasFinalSnapshot]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const logMilestone = async (tag: string, metadata?: Record<string, any>) => {
    if (!currentSessionId) return;
    try {
      await supabase.from('session_milestones').insert({
        session_id: currentSessionId,
        milestone_tag: tag,
        metadata
      });
    } catch (e) {
      console.warn("Milestone log failed", e);
    }
  };

  const generateSnapshot = () => {
    // Check if there are any messages
    if (messages.length === 0) {
      return createFallbackSnapshot('empty_session');
    }
    
    // Get the last AI message
    const lastAIMessage = [...messages].reverse().find(msg => !msg.isUser);
    
    if (lastAIMessage) {
      // Try to extract a snapshot from the AI's message
      const snapshotMatch = lastAIMessage.text.match(/\[SNAPSHOT\][\s\S]*?(?=\n\n|$)/);
      
      if (snapshotMatch) {
        // Return the exact snapshot as provided by the AI
        return snapshotMatch[0];
      }
    }
    
    // If no snapshot found, create a fallback
    return createFallbackSnapshot(getLastUserKeyword());
  };

  // Function to request a snapshot from the AI during conversation
  const requestManualSnapshot = async (isFinal: boolean = false) => {
    setIsGeneratingSnapshot(true);
    
    try {
      console.log(`Requesting ${isFinal ? "Final" : "System"} Snapshot...`);
      
      // 1. CALL THE DEDICATED SNAPSHOT ENDPOINT
      const res = await fetch('/api/generate-snapshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ history: messages }),
      });

      if (res.ok) {
        const data = await res.json();
        
        // 2. FORMAT JSON INTO UI STRING
        // The backend gives us objects; we must build the "Artifact" string.
        const formattedSnapshot = `${isFinal ? "[FINAL] " : ""}[SNAPSHOT] Session #${data.session_hex || '8F3A21'}
- Pattern: ${data.log?.pattern || 'unknown_loop'}
- Source: ${data.log?.source || 'input_stream'}
- Status: ${data.log?.status || 'analysis_complete'}

${data.poem?.line_1 || 'The system observes itself.'}
${data.poem?.line_2 || 'The noise fades into the signal.'}

[Stillpoint: htop for the mind]`;

        // 3. UPDATE STATE & OPEN MODAL
        setSnapshotData(formattedSnapshot);
        if (isFinal) {
          setHasFinalSnapshot(true);
        }
        setIsSnapshotModalOpen(true);
        
        logMilestone(isFinal ? 'Final_Snapshot_Generated' : 'Manual_Snapshot_Generated');

      } else {
        throw new Error(`Server Error: ${res.status}`);
      }

    } catch (err) {
      console.error("Snapshot Failed (Engaging Fallback):", err);
      
      // 4. FAIL-SAFE SIMULATION MODE
      // If backend fails, generate a local fallback so the user STILL gets a result.
      const fallback = isFinal
        ? `[FINAL] ${createFallbackSnapshot('simulation_override')}`
        : createFallbackSnapshot('simulation_override');
      
      // Update state and open modal with fallback data
      setSnapshotData(fallback);
      if (isFinal) {
        setHasFinalSnapshot(true);
      }
      setIsSnapshotModalOpen(true);
      
      // Optional: Alert the user (or keep it silent if you prefer smooth UX)
      // alert("Note: Running in Simulation Mode (Offline)");
    } finally {
      setIsGeneratingSnapshot(false);
    }
  };

  const createFallbackSnapshot = (sourceKeyword: string) => {
    // Extract a substring of the session ID (last 6 characters)
    const sessionSubstring = currentSessionId.slice(-6);
    const randomHex = Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    
    return `[SNAPSHOT] Session #${sessionSubstring || randomHex}
- Pattern: recursive_thought_loop
- Source: ${sourceKeyword}
- Status: manual_override

The storm you felt was real.
The sky that noticed it remains.

[Stillpoint: htop for the mind]`;
  };
  
  const getLastUserKeyword = (): string => {
    // Find the last user message
    const lastUserMessage = [...messages].reverse().find(msg => msg.isUser);
    
    if (!lastUserMessage) return 'no_input';
    
    // Extract a meaningful keyword from the last user message
    const text = lastUserMessage.text.toLowerCase();
    const commonKeywords = ['anxiety', 'stress', 'worry', 'fear', 'overwhelm', 'stuck', 'loop', 'thought', 'mind'];
    
    for (const keyword of commonKeywords) {
      if (text.includes(keyword)) return keyword;
    }
    
    // If no keyword found, return the first word that's at least 4 characters
    const words = text.split(/\s+/).filter(word => word.length >= 4);
    return words.length > 0 ? words[0] : 'unspecified';
  };

  const handleSnapshotClick = async () => {
    // Check if the last AI message already contains a snapshot
    const lastAIMessage = [...messages].reverse().find(msg => !msg.isUser);
    
    if (lastAIMessage) {
      const snapshotMatch = lastAIMessage.text.match(/\[SNAPSHOT\][\s\S]*?(?=\n\n|$)/);
      
      if (snapshotMatch) {
        // Scenario A: AI already generated a snapshot, extract it
        // Update snapshot data state
        setSnapshotData(snapshotMatch[0]);
        
        // Open the modal instead of copying directly
        setIsSnapshotModalOpen(true);
        
        logMilestone('Snapshot_Viewed');
      } else {
        // Scenario B: Manual trigger during conversation
        requestManualSnapshot();
      }
    } else {
      // Fallback if no AI messages yet
      const snapshot = createFallbackSnapshot('early_snapshot');
      
      // Update snapshot data state
      setSnapshotData(snapshot);
      
      // Open the modal instead of copying directly
      setIsSnapshotModalOpen(true);
      
      logMilestone('Early_Snapshot_Viewed');
    }
  };

  const handleNewChat = () => {
    // Clear messages and generate a new session ID
    setMessages([]);
    const newSessionId = crypto.randomUUID();
    setCurrentSessionId(newSessionId);
    localStorage.setItem('stillpoint_session_id', newSessionId);
    
    // Reset states
    setHasDoneReset(false);
    setHasShifted(false);
    setShowProModal(false);
    setSnapshotCopied(false);
    setSnapshotData(null);
    setHasFinalSnapshot(false);
    
    logMilestone('Session_Restarted');
  };

  // --- ANALYTICS LOGGER (New Feature) ---
  const logAnalytics = async (userText: string, aiResponseTime: number) => {
    if (!currentSessionId) return;
    
    // Calculate accurate message index from Ref
    const msgIndex = messagesRef.current.filter(m => m.isUser).length + 1;

    try {
      await supabase.from('session_analytics').insert({
        session_id: currentSessionId,
        message_index: msgIndex,
        user_word_count: userText.trim().split(/\s+/).length,
        ai_response_time_ms: aiResponseTime,
        protocol_active: hasDoneReset ? 'Recursion' : 'Standard'
      });
    } catch (err) {
      console.warn("Analytics logging failed - Table might not exist yet", err);
    }
  };

  // --- BURST MODE LOGIC ---
  useEffect(() => {
    if (pendingUserMessages.length > 0 && !hasUnsentInput) {
      if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
      
      burstTimerRef.current = setTimeout(() => {
        triggerAIResponse(); 
        setPendingUserMessages([]); 
      }, 2000); 
    } else {
      if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
    }
    return () => {
      if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
    };
  }, [pendingUserMessages, hasUnsentInput]);

  // --- HELPER: SQUASH CONSECUTIVE MESSAGES & FIX HISTORY ---
  const normalizeHistory = (msgs: Message[]) => {
    const apiMessages: { role: string; content: string }[] = [];
    let lastRole = null;
    const recentMsgs = msgs.slice(-20);

    for (const m of recentMsgs) {
      const role = m.isUser ? 'user' : 'assistant';
      if (role === lastRole && apiMessages.length > 0) {
        apiMessages[apiMessages.length - 1].content += "\n\n" + m.text;
      } else {
        apiMessages.push({ role, content: m.text });
        lastRole = role;
      }
    }

    if (apiMessages.length > 0 && apiMessages[0].role === 'assistant') {
      apiMessages.shift();
    }

    return apiMessages;
  };

  const triggerAIResponse = async () => {
    setIsAIProcessing(true);
    const startTime = performance.now(); // Start Timer
    
    const currentMessages = messagesRef.current;
    const fullConversation = normalizeHistory(currentMessages);

    if (fullConversation.length === 0) {
      setIsAIProcessing(false);
      return;
    }

    const lastMsg = fullConversation[fullConversation.length - 1];
    const lastUserText = lastMsg.role === 'user' ? lastMsg.content : "";

    // Logic Checks
    if (hasDoneReset && !hasShifted) {
      const lower = lastUserText.toLowerCase();
      if (lower.includes('i see') || lower.includes('makes sense') || lower.includes('wow') || lower.includes('lighter')) {
        setHasShifted(true);
        logMilestone('Subject_Object_Shift');
      }
    }

    const isAdminTrigger = lastUserText.startsWith("/unlock 7f3a9b2c");
    let finalSystemPrompt = "";

    if (isAdminTrigger) {
      finalSystemPrompt = ADMIN_OVERRIDE_PROMPT;
    } else {
      const recursionContext = hasDoneReset ? `\n\n${RECURSION_LOGIC}` : "";
      finalSystemPrompt = `${IMMUTABLE_PERSONA_CORE}\n\n${MODERN_MODE_INSTRUCTIONS}${recursionContext}\n\n${COMMUNICATION_DYNAMICS}\n\n${PANIC_PROTOCOL}`;
    }

    try {
      const res = await fetch('/api/anthropic/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: fullConversation, 
          temperature: 0.6,
          max_tokens: 1024,
          system: finalSystemPrompt,
        }),
      });

      const data: ApiResponse<ChatCompletionResponse> = await res.json();
      const endTime = performance.now(); // Stop Timer

      if (data.success) {
        // Log Analytics on Success
        logAnalytics(lastUserText, Math.round(endTime - startTime));

        setMessages((prev) => [...prev, {
          id: `ai-${Date.now()}`,
          text: data.data.message,
          timestamp: formatTime(new Date()),
          isUser: false,
        }]);
      } else {
        console.error("AI Error:", data.error);
        setError(data.error.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setError(err instanceof Error ? err.message : 'Connection Error');
    } finally {
      setIsAIProcessing(false);
    }
  };

  const handleSendMessage = (text: string) => {
    if (!isHydrated) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text,
      timestamp: formatTime(new Date()),
      isUser: true,
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setPendingUserMessages(prev => [...prev, text]);
  };

  const handleStarterClick = (userText: string, aiScript: string) => {
    const userMessage: Message = { id: `user-${Date.now()}`, text: userText, timestamp: formatTime(new Date()), isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setIsAIProcessing(true);

    setTimeout(() => {
      setMessages(prev => [...prev, { id: `ai-${Date.now()}`, text: aiScript, timestamp: formatTime(new Date()), isUser: false }]);
      setIsAIProcessing(false);
    }, 1500);
  };

  const handleSomaticReset = async () => {
    const triggerText = "I need a 3-Breath Reset.";
    setMessages(prev => [...prev, { id: `reset-trigger-${Date.now()}`, text: triggerText, timestamp: formatTime(new Date()), isUser: true }]);
    setIsAIProcessing(true);

    setTimeout(() => setMessages(prev => [...prev, { id: `s1`, text: "Stop. Inhale deeply... Hold it for 3 seconds.", timestamp: formatTime(new Date()), isUser: false }]), 1000);
    setTimeout(() => setMessages(prev => [...prev, { id: `s2`, text: "Exhale slowly. Drop your jaw. Drop your shoulders.", timestamp: formatTime(new Date()), isUser: false }]), 4500);
    setTimeout(() => setMessages(prev => [...prev, { id: `s3`, text: "Now, listen to the room. What is the farthest sound you can hear?", timestamp: formatTime(new Date()), isUser: false }]), 8000);
    setTimeout(() => {
      setMessages(prev => [...prev, { id: `s4`, text: "The sound is happening. The breath is happening. The 'Stress' is just another thing happening.\n\nYou are the one watching it all.", timestamp: formatTime(new Date()), isUser: false }]);
      setIsAIProcessing(false);
      setHasDoneReset(true);
      logMilestone('Somatic_Reset_Complete');
    }, 12500);
  };

  const handleJoinInnerCircle = () => {
    logMilestone('Monetization_Intent');
    window.open("https://tally.so/r/aQ2qG9", "_blank");
  };

  if (!isHydrated) return <div className="h-screen flex items-center justify-center">Loading Stillpoint...</div>;

  // Snapshot Modal Component
  const SnapshotModal = () => {
    const copyToClipboard = async () => {
      if (!snapshotData) return;
      
      try {
        await navigator.clipboard.writeText(snapshotData);
        setSnapshotCopied(true);
        setShowToast(true);
        
        setTimeout(() => {
          setSnapshotCopied(false);
          setShowToast(false);
        }, 3000);
      } catch (err) {
        console.warn("Failed to copy snapshot:", err);
      }
      
      // Close the modal after copying
      setIsSnapshotModalOpen(false);
    };

    return (
      <div className={`fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 ${isSnapshotModalOpen ? 'animate-fade-in' : 'hidden'}`}>
        <div className="bg-stone-900 border border-stone-700 rounded-md max-w-2xl w-full mx-4 shadow-2xl overflow-hidden">
          {/* Terminal Header */}
          <div className="bg-stone-800 px-4 py-2 border-b border-stone-700 flex justify-between items-center">
            <div className="text-green-400 font-mono text-sm">
              &gt; SYSTEM_SNAPSHOT_GENERATED
            </div>
            <button
              onClick={() => setIsSnapshotModalOpen(false)}
              className="text-stone-400 hover:text-white"
            >
              [ X ]
            </button>
          </div>
          
          {/* Terminal Content */}
          <div className="p-4 font-mono">
            <pre className="whitespace-pre-wrap text-stone-300 text-sm overflow-auto max-h-96">
              {snapshotData || 'No snapshot data available.'}
            </pre>
          </div>
          
          {/* Terminal Footer */}
          <div className="bg-stone-800 px-4 py-3 border-t border-stone-700 flex justify-end">
            <button
              onClick={copyToClipboard}
              className="px-4 py-2 bg-stone-700 hover:bg-stone-600 text-white rounded font-mono text-sm transition-colors flex items-center gap-2"
            >
              [ Copy to Clipboard ]
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-stone-50">
      <ChatHeader
        onMenuClick={() => setIsHistoryOpen(true)}
        onSettingsClick={() => setIsSettingsOpen(true)}
        isOnline={true}
        languageMode="modern"
      />

      <main className="flex-1 overflow-y-auto px-4 py-6" style={{ scrollbarWidth: 'thin' }}>
        <div className="max-w-4xl mx-auto pb-24">
          {messages.length === 0 ? (
            <EmptyState onStarterClick={handleStarterClick} />
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg.text} timestamp={msg.timestamp} isUser={msg.isUser} />
              ))}
              
              {(isAIProcessing || pendingUserMessages.length > 0) && (
                <MessageBubble message="" timestamp="" isUser={false} isTyping={true} />
              )}
              
              {hasShifted && !isLimitReached && (
                <div className="flex justify-center mt-6 mb-4 animate-fade-in">
                  <button onClick={handleJoinInnerCircle} className="px-6 py-3 bg-slate-800 text-white rounded-full text-sm font-medium hover:bg-slate-700 transition-all shadow-lg flex items-center gap-2">
                    <span>✨</span> Join the Inner Circle
                  </button>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </main>

      <div className="relative">
        {!isLimitReached && (
          <div className="flex justify-center mb-4 pointer-events-none">
            <button onClick={handleSomaticReset} disabled={isAIProcessing} className="pointer-events-auto flex items-center gap-2 px-5 py-2.5 rounded-full shadow-md transition-all transform hover:scale-105 active:scale-95 bg-orange-100 text-orange-900 border border-orange-200 hover:bg-orange-200">
              <span className="text-lg">🌬️</span>
              <span className="font-medium text-sm">3-Breath Reset</span>
            </button>
          </div>
        )}

        {isLimitReached ? (
          <div className="p-4 bg-background border-t border-border animate-fade-in-up">
            <div className="max-w-4xl mx-auto bg-stone-900 rounded-xl p-6 text-white shadow-lg font-mono">
              <h3 className="text-xl mb-3 border-b border-stone-700 pb-2">System Report</h3>
              
              {/* Snapshot Section */}
              <div className="bg-stone-800 p-3 rounded mb-4 text-left text-sm">
                <pre className="whitespace-pre-wrap text-stone-300 font-mono text-xs leading-relaxed">
                  {isGeneratingSnapshot
                    ? "SYSTEM KERNEL: Re-indexing full session history... [ |||||| ]"
                    : (snapshotData || generateSnapshot())
                  }
                </pre>
              </div>
              
              {/* View Snapshot Button */}
              <button
                onClick={handleSnapshotClick}
                className={`w-full mb-4 px-4 py-2 rounded bg-stone-700 hover:bg-stone-600 transition-colors flex items-center justify-center gap-2`}
                disabled={isGeneratingSnapshot}
              >
                <span>
                  {isGeneratingSnapshot ?
                    '⏳ Generating Snapshot...' : '📸 View System Snapshot'}
                </span>
              </button>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={handleNewChat}
                  className="px-3 py-2 bg-stone-700 hover:bg-stone-600 rounded text-sm transition-colors"
                >
                  Restart Session (Clear Cache)
                </button>
                <button
                  onClick={() => setShowProModal(true)}
                  className="px-3 py-2 bg-stone-700 hover:bg-stone-600 rounded text-sm transition-colors"
                  onMouseDown={() => logMilestone('Monetization_Intent_Pro')}
                >
                  Save Session to Memory (Pro)
                </button>
              </div>
              
              {/* Vibe Check */}
              <div className="text-center mt-6">
                <p className="text-stone-400 text-sm mb-2">Did this debug your loop?</p>
                
                {feedbackState === 'initial' && (
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => {
                        setFeedbackState('yes');
                        logMilestone('Feedback_Yes');
                      }}
                      className="px-4 py-1 bg-stone-700 hover:bg-stone-600 rounded text-sm"
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => {
                        setFeedbackState('no');
                        logMilestone('Feedback_No');
                      }}
                      className="px-4 py-1 bg-stone-700 hover:bg-stone-600 rounded text-sm"
                    >
                      No
                    </button>
                  </div>
                )}
                
                {(feedbackState === 'yes' || feedbackState === 'no') && (
                  <div className="mt-2">
                    <textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder={feedbackState === 'yes' ? "What optimized the process?" : "Where was the latency?"}
                      className="w-full p-2 bg-stone-800 border border-stone-700 rounded text-white text-sm mb-2"
                      rows={3}
                    />
                    <button
                      onClick={() => {
                        logMilestone('Feedback_Commit', {
                          feedback: feedbackText,
                          rating: feedbackState
                        });
                        setFeedbackState('submitted');
                      }}
                      className="px-4 py-1 bg-stone-700 hover:bg-stone-600 rounded text-sm"
                      disabled={!feedbackText.trim()}
                    >
                      Submit Feedback
                    </button>
                  </div>
                )}
                
                {feedbackState === 'submitted' && (
                  <p className="text-green-400 text-sm mt-2">Thank you for your feedback!</p>
                )}
              </div>
              
              {/* Pro Modal */}
              {showProModal && !showProSuccess && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-stone-800 p-6 rounded-lg max-w-md w-full">
                    <h4 className="text-lg mb-3">Pro is currently in Alpha</h4>
                    <p className="text-stone-300 mb-4 text-sm">Enter email to get early access.</p>
                    <input
                      type="email"
                      value={proEmail}
                      onChange={(e) => setProEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full p-2 mb-3 bg-stone-700 border border-stone-600 rounded text-white"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setShowProModal(false)}
                        className="px-4 py-2 bg-stone-700 hover:bg-stone-600 rounded"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (proEmail.trim()) {
                            logMilestone('Monetization_Intent', { email: proEmail });
                            setShowProSuccess(true);
                          }
                        }}
                        className="px-4 py-2 bg-stone-600 hover:bg-stone-500 rounded"
                        disabled={!proEmail.trim()}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Pro Success Modal */}
              {showProModal && showProSuccess && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-stone-800 p-6 rounded-lg max-w-md w-full text-center">
                    <div className="text-green-400 text-4xl mb-3">✓</div>
                    <h4 className="text-lg mb-3">System State Saved</h4>
                    <p className="text-stone-300 mb-4 text-sm">You'll be notified when Pro access is available.</p>
                    <button
                      onClick={() => {
                        setShowProModal(false);
                        setShowProSuccess(false);
                        setProEmail('');
                      }}
                      className="px-4 py-2 bg-stone-600 hover:bg-stone-500 rounded"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <MessageInput 
            onSendMessage={handleSendMessage} 
            onTypingStateChange={setHasUnsentInput}
            onSnapshotClick={handleSnapshotClick}
            showSnapshotButton={messages.length >= 5}
            isGeneratingSnapshot={isGeneratingSnapshot}
          />
        )}
      </div>

      <ChatHistoryDrawer isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} onSessionSelect={() => {}} currentSessionId={currentSessionId} />
      <SettingsPanel isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      
      {/* Render the Snapshot Modal */}
      <SnapshotModal />
      
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 right-4 bg-stone-800 text-white px-4 py-2 rounded shadow-lg z-50 animate-fade-in">
          System Snapshot Copied
        </div>
      )}
    </div>
  );
};

export default ChatInterfaceInteractive;